import {
  Applicant,
  ApplicantAddressType,
  ApplicationProps,
  BrokerList,
  nonIndividualMdmsQuestionsFatca,
  uboTypes,
  Broker,
} from '../redux-store/types/api-types';
import {
  allFieldMaps,
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  applicantStatusMasters,
  APPLICATION_LISTING_STATUS,
  APPLICATION_TYPE,
  ApplicationStatusBasedOnRole,
  DLCLMasters,
  IndividualApplicationStatusBasedOnRole,
  NCT_APPLICATION_STATUS,
  NCT_STATUS,
  RISK_CATEGORIZATION,
  SALT,
  SALT2,
  dlclIdFieldValidationDate,
  SourceOfFundMaster,
  USER_ROLES,
  whetherRegisterMaster,
  RISK_SALT,
  RISK_SALT2,
  YesNoMaster,
} from './constant';
import { ToWords } from 'to-words';
import { numberRegex } from './regex';
import _ from 'lodash';
import en from '../lang/en-us';
import {
  MenuItemListLink,
  NonIndividualMenuItemListLink,
} from '../components/investor-management/InvestorManagementLayout';
import {
  ApplicationNctProps,
  DifferenceType,
  PendingInvestorResponseType,
} from '../redux-store/types/nct';

export const maskMobileNumber = (mobileNumber: string): string => {
  if (!mobileNumber) {
    return '';
  }
  return (
    mobileNumber.slice(0, 2) +
    mobileNumber.slice(2, mobileNumber.length - 2).replace(/\d/g, '*') +
    mobileNumber.slice(mobileNumber.length - 2)
  );
};

export function getOccupationType(occupation?: string | null): boolean {
  return [
    'PVT.SECTOR SERVICE',
    'PUBLIC SECTOR',
    'GOVT.SERVICE',
    'HOUSEWIFE',
    'DEFENCE',
    'PROFESSIONAL',
    'RETIRED',
    'BUSINESS',
    'AGRICULTURE',
    'STUDENT',
    'FOREX DEALER',
    '',
  ].includes(occupation || '');
}

export const getApplicantName = (index: number, isNomineeOrBank?: boolean): string => {
  if (index === 1) {
    return isNomineeOrBank ? 'First' : 'First/Sole';
  } else if (index === 2) {
    return 'Second';
  } else {
    return 'Third';
  }
};

export const minDateForContributor = (): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  return date;
};

export const getFirstHolderName = (applicants: Partial<Applicant>[]): string => {
  return (
    applicants.find((applicant) => ['firstapplicant', '1'].includes(applicant.applicant_type || ''))
      ?.name || 'N/A'
  );
};

export const getApplicantType = (applicantType: string | undefined): string => {
  if (applicantType) {
    if (['firstapplicant', '1'].includes(applicantType)) {
      return '1';
    } else if (['secondapplicant', '2'].includes(applicantType)) {
      return '2';
    } else {
      return '3';
    }
  }
  return '';
};

export const formatToIndianCurrency = (value: number | null): string => {
  const options = { style: 'currency', currency: 'INR' };
  if (value) {
    return new Intl.NumberFormat('en-IN', options).format(value);
  }
  return '';
};

export function toExactSubstr(str: string): string {
  const a = str.split('.');
  if (a.length == 2) {
    return a[0].concat('.', a[1].substr(0, 2));
  }
  return str;
}

export const formatCurrency = (value?: number | null): string => {
  const formats = ['K', 'L', 'Cr'];
  let amount: any = Number(value);
  formats.some((key, i) => {
    const size = Math.pow(10, i === 0 ? 3 : 3 - 1);
    if (size <= amount) {
      amount = toExactSubstr(`${amount / size}`);
      if (amount >= 100 && i < formats.length - 1) {
        i++;
      } else {
        // amount = (amount * decPlaces) / decPlaces;
        amount += ' ' + formats[i];
        return true;
      }
    } else {
      return true;
    }
  });
  return amount;
};

export const EIGHTEEN_YEARS_IN_MILLISECONDS = 18 * 31556926 * 1000; //  18 * no of seconds in a Year * 1000

export const isMinor = (dateOfBirth = ''): boolean => {
  return new Date().getTime() - new Date(dateOfBirth).getTime() < EIGHTEEN_YEARS_IN_MILLISECONDS;
};

export const getAddressFields = (addressType: string): ApplicantAddressType => {
  return {
    address1: '',
    address2: '',
    // address3: '',
    city: '',
    state: '',
    country: addressType === 'overseas' ? '' : 'INDIA',
    pincode: '',
    permanentAddressSameAsCorresponding: false,
    address_type: addressType,
    isActive: true,
    fetchedFromKRA: null,
  };
};

export const getAddressData = (
  addressType: string,
  addresses: Partial<ApplicantAddressType>[] | undefined
): Partial<ApplicantAddressType> => {
  return (
    addresses?.find((address) => address.address_type === addressType) ||
    getAddressFields(addressType)
  );
};

export function getRelation(relationValue?: string | null): boolean {
  return ['Mother', 'Father', 'Daughter', 'Son', 'Spouse', 'Brother', 'Sister', ''].includes(
    relationValue || ''
  );
}

export function getSourceOfFund(sourceValue?: string | null): boolean {
  return [
    'employment_salary',
    'Business_consultancy_income',
    'dividend_interest',
    'donation_grant',
    'pension_retirement_funds',
    'shareholders_funds',
    'reserves_retained_earnings',
    'sales_of_assets',
    'sales_of_shares_securities_investment',
    'gifts',
    'partners_members_funds',
    'inheritance',
  ].includes(sourceValue || '');
}

export const saveForLater = (
  role: string,
  id?: string,
  applicant1ReferenceId?: string | null
): string => {
  return role === USER_ROLES.INVESTOR
    ? `/investment-details/${applicant1ReferenceId}/application-details`
    : [USER_ROLES.POAAPPROVER, USER_ROLES.AMCAPPROVER, USER_ROLES.KFIN_REVIEWER].includes(
        role as USER_ROLES
      )
    ? `/application-details/${id}`
    : '/applications';
};

export const maxAge = (dateOfBirth = ''): boolean => {
  const thisYear = new Date().getFullYear();
  return thisYear - new Date(dateOfBirth).getFullYear() > 125;
};

export function currencyConversion(num?: string | number | null): string | null {
  if (!num) return '';
  const toWords = new ToWords();
  const words = toWords.convert(Number(num), { currency: true });
  return words;
}

export function checkIfApplicationIsNonIndividual({
  applicationType,
}: ApplicationProps | ApplicationNctProps | PendingInvestorResponseType): boolean {
  return applicationType === APPLICATION_TYPE.NON_INDIVIDUAL;
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const countryCodes = require('country-codes-list');
export const getCountryCodes = (): { label: string; key: string; countryCode: string }[] => {
  const countryCodesObject: { [key: string]: string } = countryCodes.customList(
    'countryCode',
    '{countryNameEn}: +{countryCallingCode}'
  );

  const modifiedCodes = Object.keys(countryCodesObject).map((cc) => ({
    label: countryCodesObject[cc],
    key: countryCodesObject[cc].split(':')[1].trim(),
    countryCode: cc,
  }));
  return modifiedCodes;
};

// eslint-disable-next-line
export const preventSpecialCharacters = (e: any) => {
  if (
    !((e.ctrlKey || e.metaKey) && e.keyCode == 67) &&
    !((e.ctrlKey || e.metaKey) && e.keyCode == 86) &&
    !((e.ctrlKey || e.metaKey) && e.keyCode == 88) &&
    !((e.ctrlKey || e.metaKey) && e.keyCode == 90) &&
    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key) &&
    !numberRegex.test(e.key)
  ) {
    e.preventDefault();
  }
};
export function allowOnlyNumbers(e: any): void {
  if (['-', '+', 'e', 'E'].includes(e.key)) {
    e.preventDefault();
  }
}
export const permanentAddressSameAsCorresponding = (
  updatedCorrespondingAddress: Partial<ApplicantAddressType>,
  addressType: string,
  addresses: Partial<ApplicantAddressType>[] | undefined
): Partial<ApplicantAddressType> => {
  // eslint-disable-next-line
  const { address1, address2, city, pincode, state, country, ...rest } =
    updatedCorrespondingAddress;
  return {
    ...getAddressData(addressType, addresses),
    address1: address1,
    address2: address2,
    city: city,
    pincode: pincode,
    state: state,
    country: country,
    address_type: addressType,
  };
};
export const _updatedAddresses = (
  updatedAddresses: {
    nationality?: string | null | undefined;
    status?: string | null | undefined;
    permanent?: Partial<ApplicantAddressType> | undefined;
    correspondence?: Partial<ApplicantAddressType> | undefined;
    overseas?: Partial<ApplicantAddressType> | undefined;
    [key: string]: string | Partial<ApplicantAddressType> | null | undefined;
  },
  applicant: Partial<Applicant>
): Partial<ApplicantAddressType>[] => {
  const { nationality, status, ...addressProps } = updatedAddresses;
  return Object.keys(addressProps)
    .map((addressType) => {
      // if (applicant.nationality?.toLowerCase() === 'indian' && addressType === 'overseas') {
      //   return {};
      // }
      if (
        addressType === 'permanent' &&
        addressProps['overseas']?.permanentAddressSameAsCorresponding
      ) {
        return permanentAddressSameAsCorresponding(
          addressProps['overseas'],
          addressType,
          applicant.addresses
        );
      }
      if (
        addressType === 'permanent' &&
        addressProps['correspondence']?.permanentAddressSameAsCorresponding &&
        !addressProps['overseas']
      ) {
        return permanentAddressSameAsCorresponding(
          addressProps['correspondence'],
          addressType,
          applicant.addresses
        );
      }
      if (
        Object.keys(addressProps).length === 1 &&
        addressProps['overseas']?.permanentAddressSameAsCorresponding
      ) {
        return [
          {
            ...permanentAddressSameAsCorresponding(
              addressProps['overseas'],
              'permanent',
              applicant.addresses
            ),
          },
          {
            ...getAddressData(addressType, applicant.addresses),
            ...(addressProps[addressType] as unknown as Partial<ApplicantAddressType>),
            address_type: addressType,
          },
        ];
      }
      if (
        Object.keys(addressProps).length === 1 &&
        addressProps['correspondence']?.permanentAddressSameAsCorresponding
      ) {
        return [
          {
            ...permanentAddressSameAsCorresponding(
              addressProps['correspondence'],
              'permanent',
              applicant.addresses
            ),
          },
          {
            ...getAddressData(addressType, applicant.addresses),
            ...(addressProps[addressType] as unknown as Partial<ApplicantAddressType>),
            address_type: addressType,
          },
        ];
      }
      return {
        ...getAddressData(addressType, applicant.addresses),
        ...(addressProps[addressType] as unknown as Partial<ApplicantAddressType>),
        address_type: addressType,
      };
    })
    .flat()
    .filter((address) => Object.keys(address).length);
};
export const applyRoleBasedStatus = (role: any): boolean => {
  return [USER_ROLES.RM, USER_ROLES.SUBDISTRIBUTOR, USER_ROLES.DISTRIBUTOR].includes(role);
};

export const checkKraBasedOnStatus = (status: any): boolean => {
  return ![
    APPLICATION_LISTING_STATUS.invitationexpired,
    APPLICATION_LISTING_STATUS.signed,
    APPLICATION_LISTING_STATUS.completed,
    APPLICATION_LISTING_STATUS.rejected,
  ].includes(APPLICATION_LISTING_STATUS[status]);
};

export const applicationComparison = (
  existingApplication: Partial<ApplicationProps> | null,
  updatedApplication: Partial<ApplicationProps>
): boolean => {
  return _.isEqual(existingApplication, updatedApplication);
};

export const sendApplication_Nri = (application?: ApplicationProps | null): boolean | undefined => {
  return application?.applicants.map((applicant) => applicant.status).includes('NRI');
};

export const checkForCorrespondenceAddress = (
  nationality: string | null | undefined,
  status: string | null | undefined
): boolean => {
  return (
    nationality?.toLowerCase() === 'indian' &&
    applicantStatusMasters[status as string] === applicantStatusMasters.Individual
  );
};

export const setUpFeeCalculations = (
  commitmentAmount: number,
  setupFeePercentage: number
): { feeAmount: number; feeGst: number; totalSetupFee: number } => {
  const setupFeeAmount = (Number(commitmentAmount) * Number(setupFeePercentage)) / 100;
  const GstForSetupFee = (setupFeeAmount * 18) / 100;
  return {
    feeAmount: Number(setupFeeAmount),
    feeGst: Number(GstForSetupFee),
    totalSetupFee: Number(setupFeeAmount + GstForSetupFee),
  };
};

export const checkAddressField = (data?: string) => {
  if (data) {
    if (data.includes(' ,')) {
      return data.split(' ,').join(',') + ' ';
    }
    return data.includes(',') ? data + ' ' : data + ', ';
  }
  return '';
};

export const getBankAddress = (
  address1: string | undefined,
  address2: string | undefined,
  address3: string | undefined
): string => {
  return `${address2 ? checkAddressField(address1) || '' : address1 || '' || ''}${
    address3 ? checkAddressField(address2) || '' : address2 || ''
  }${address3 || ''}`;
};

export const numToAlpMonth = (numMonth: any, month: any) => {
  numMonth?.map((monthNum: any) => {
    if (monthNum === 1) {
      month.push('Jan');
    } else if (monthNum === 2) {
      month.push('Feb');
    } else if (monthNum === 3) {
      month.push('Mar');
    } else if (monthNum === 4) {
      month.push('Apr');
    } else if (monthNum === 5) {
      month.push('May');
    } else if (monthNum === 6) {
      month.push('Jun');
    } else if (monthNum === 7) {
      month.push('Jul');
    } else if (monthNum === 8) {
      month.push('Aug');
    } else if (monthNum === 9) {
      month.push('Sep');
    } else if (monthNum === 10) {
      month.push('Oct');
    } else if (monthNum === 11) {
      month.push('Nov');
    } else if (monthNum === 12) {
      month.push('Dec');
    }
  });
};

export const removeSingleQuote = (value: string | null | undefined): any => {
  if (value && value.includes("'")) return value.split("'").join('');
  else return value;
};

export function removeEqualOperator(value = ''): string {
  const lastTwoIndex = value.length - 2;
  let encodeString = value.slice(0, lastTwoIndex);
  const slicedStr = value.slice(lastTwoIndex, value.length);
  slicedStr.split('').forEach((char) => {
    if (char !== '=') {
      encodeString += char;
    }
  });
  return encodeString;
}

export function encodeBase64(value: string): string {
  const encodeValue = btoa(value);
  const encodeSubString = removeEqualOperator(encodeValue);
  const encodePayload = btoa(`${SALT}${encodeSubString}${SALT2}`);
  return encodePayload;
}

export function getNomineeRelation(relationValue?: string | null): boolean {
  return ['MOTHER', 'FATHER', 'DAUGHTER', 'SON', 'SPOUSE', 'BROTHER', 'SISTER', ''].includes(
    relationValue || ''
  );
}
export const getdpIdField = (value: string): boolean => {
  return (
    DLCLMasters[value || ''] === DLCLMasters.cdsl || DLCLMasters[value || ''] === DLCLMasters.nsdl
  );
};

export const clearclIdField = (value: string): boolean => {
  return (
    DLCLMasters[(value as string) || ''] === DLCLMasters.none ||
    DLCLMasters[(value as string) || ''] === DLCLMasters.cdsl
  );
};

export const cleardpIdField = (value: string): boolean => {
  return DLCLMasters[(value as string) || ''] === DLCLMasters.none;
};

export const getclIdField = (value: string): boolean => {
  return DLCLMasters[value || ''] === DLCLMasters.nsdl;
};

export const isCDSL = (value: string): boolean => {
  return DLCLMasters[(value as string) || ''] === DLCLMasters.cdsl;
};

export const checkValidationBasedOnDate = (
  date: string | number | Date, // this is the application creation date
  validationStartDate: string // date from which you want to make your changes
): boolean => {
  if (!date) return false;
  const dateForCompare = date ? new Date(date) : new Date();
  const dateForApplyValidations = new Date(validationStartDate);
  return dateForCompare.getTime() >= dateForApplyValidations.getTime();
};

export const dlclIdFieldMandatory = () => {
  return checkValidationBasedOnDate(new Date(), dlclIdFieldValidationDate);
};

export function otherNameOfBroker(nameOfBroker: string): boolean {
  return nameOfBroker?.toLocaleLowerCase() === 'others';
}

export function nameOfBrokerExists(brokerList: Broker[], nameOfBroker: string): boolean {
  return brokerList?.map((broker) => broker.key).includes(nameOfBroker || '');
}

export const sortListKeepOthersLast = (brokerList: Broker[] = []) => {
  // Separate "Others" from the rest of the items
  const others = brokerList.filter((item) => item.key === 'others');
  const rest = brokerList
    .filter((item) => item.key !== 'others')
    .sort((a, b) => a.key?.localeCompare(b.key));

  // Sort the rest of the items and append "Others" at the end
  return [...rest.sort(), ...others];
};
export const handleDownload = async (fileUrl: string, fileName: string): Promise<void> => {
  try {
    const url = new URL(fileUrl, window.location.origin);

    const isSameOrigin = url.origin === window.location.origin;

    if (isSameOrigin) {
      // Same-origin: rename supported
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error('Unable to download file');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      URL.revokeObjectURL(blobUrl);
    } else {
      // Cross-origin (e.g. S3): no fetch => no CORS issue
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

export function splitMicroWords(input: string): string {
  // First, split by underscores if any
  const snakeParts = input.split('_');
  // Then for each part, split camelCase using regex
  const words = snakeParts.flatMap((part) =>
    part.split(/(?=[A-Z])/).map((word) => word.toLowerCase())
  );
  // First convert array into string, uppercase, replace comma with space
  return words.join(' ').toUpperCase();
}

export const getFullAddress = (
  address: Partial<Record<string, any>>,
  fieldOrder: string[] = ['address1', 'address2', 'city', 'state', 'country', 'pincode']
): string => {
  return fieldOrder
    .map((key) => {
      const value = address[key];
      return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    })
    .filter((val) => val)
    .join(', ');
};

export const getCurrentStep = (applicationType: string, current: string): number => {
  const navList =
    applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualMenuItemListLink
      : MenuItemListLink;
  const normalizedCurrent = current.split('/').pop();
  return navList.findIndex((item) => item === normalizedCurrent) + 1;
};

export const navigateToNextStep = (
  currentPath: string,
  role: string,
  id: string,
  isSaveLater = false,
  applicant1ReferenceId: string,
  applicationType: string
): string => {
  const navList =
    applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualMenuItemListLink
      : MenuItemListLink;
  const normalizedCurrent = currentPath.split('/').pop();
  const currentIndex = navList.findIndex((path) => path === normalizedCurrent);
  const nextIndex = currentIndex + 1;
  if (nextIndex < navList.length && !isSaveLater) {
    return navList[nextIndex];
  }
  // Below Logic is for last page navigation
  // Handle INVESTOR role
  if (role === USER_ROLES.INVESTOR) {
    return `/investment-details/${applicant1ReferenceId}/application-details`;
  }

  // Handle POA/AMC Approver role
  if (
    [USER_ROLES.POAAPPROVER, USER_ROLES.AMCAPPROVER, USER_ROLES.KFIN_REVIEWER].includes(
      role as USER_ROLES
    )
  ) {
    return `/application-details/${id}`;
  }

  // Handle Save Later path
  if (isSaveLater) {
    return `/investor-management`;
  }

  // Handle based on application type
  return applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
    ? `/non-individual-kyc-preview/${id}`
    : `/individual-kyc-preview/${id}`;
};

export const navigateAfterLastPage = (
  role: string,
  id: string,
  isSaveLater: boolean,
  applicant1ReferenceId: string,
  applicationType: string
): string => {
  // Handle INVESTOR role
  if (role === USER_ROLES.INVESTOR) {
    return `/investment-details/${applicant1ReferenceId}/application-details`;
  }

  // Handle POA/AMC Approver role
  if (
    [USER_ROLES.POAAPPROVER, USER_ROLES.AMCAPPROVER, USER_ROLES.KFIN_REVIEWER].includes(
      role as USER_ROLES
    )
  ) {
    return `/application-details/${id}`;
  }

  // Handle Save Later path
  if (isSaveLater) {
    return `/applications`;
  }

  // Handle based on application type
  return applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
    ? `/non-individual-application-preview/${id}`
    : `/application-preview/${id}`;
};

export function getChangedAddressType(addressObj: ApplicantAddressType): string | null {
  const type = addressObj.address_type;

  switch (type) {
    case 'correspondence':
      return 'Correspondence Address';
    case 'permanent':
      return 'Permanent Address';
    case 'overseas':
      return 'Overseas Address';
    default:
      return null;
  }
}

export const findQuestionTextByBackendKey = (
  questions: nonIndividualMdmsQuestionsFatca,
  backendKeyToFind: string
): string | undefined => {
  for (const question of questions) {
    if (question.backend_key === backendKeyToFind) {
      return question.question_text;
    }
    if (question.sub_questions?.length) {
      const subResult = findQuestionTextByBackendKey(question.sub_questions, backendKeyToFind);
      if (subResult) return subResult;
    }
  }
  return undefined;
};

/**
 * @function truncateText - Truncates the text.
 *                  `(text: string, maxLength: number)` => `string`
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text) return 'NA';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export const displayNctRequestApprovalUI = (role: string): boolean => {
  return USER_ROLES.AMCAPPROVER === role;
};

// Extracts prefix (new/del/etc.)
export function extractAction(key: string): string {
  const [action] = key.split('_');
  return action; // "new" or "del"
}

// Formats entity name
export function autoFormatText(text: string): string {
  const [action, ...rest] = text.split('_');
  return rest
    .map((entity) => {
      const words = entity.match(/controlling|person|group|signatory/gi);
      if (words) {
        return words.map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      }
      return entity[0].toUpperCase() + entity.slice(1).toLowerCase();
    })
    .join(' ');
}

export function getFieldLabelByKey(
  key: string,
  fatcaData: nonIndividualMdmsQuestionsFatca
): string {
  for (const map of allFieldMaps) {
    if (key in map) {
      return map[key];
    }
  }
  // 2. Then try to find the question text from fatcaData
  const questionText = findQuestionTextByBackendKey(fatcaData, key);
  if (questionText) {
    return questionText;
  }
  // fallback: try formatting nicely
  return extractAction(key).includes('new') || extractAction(key).includes('del')
    ? autoFormatText(key)
    : key;
}

export const checkForCompletedApplication = (status: string): boolean => {
  return [APPLICATION_LISTING_STATUS.completed].includes(APPLICATION_LISTING_STATUS[status]);
};

export const parseIfJson = (value: any) => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed;
    } catch {
      return value; // not a JSON string, return as-is
    }
  }
  return value;
};

export const getApplicationStatus = (status: string, role: string, roleType?: string): string => {
  const entry = NCT_APPLICATION_STATUS[status];
  if (typeof entry === 'string') return entry;
  if (status === 'sent_to_amc_approver2') {
    if (role === USER_ROLES.AMCAPPROVER && roleType === 'approver1') {
      return entry.default ?? status;
    }
  }
  return entry?.[role] ?? entry?.default ?? status;
};

export const isChangeRequestPending = (changeRequestStatus: string): boolean => {
  return NCT_STATUS[changeRequestStatus] === NCT_STATUS.pending;
};

// helper to resolve risk labels
export function resolveRiskLabel(
  value?: string,
  fieldName?: string,
  brokerList?: BrokerList
): string {
  if (!value) return '';
  return (
    (fieldName === 'nameOfBroker' &&
      brokerList?.broker_list.find((broker) => broker.key === value)?.value) ||
    RISK_CATEGORIZATION[value] ||
    whetherRegisterMaster[value] ||
    SourceOfFundMaster[value] ||
    value
  );
}

export const getUboDeclarationValues = (
  uboMDMS: uboTypes,
  auditDiff: { oldValue?: string; newValue?: string; fieldName?: string },
  brokerList?: BrokerList
): { oldValue: string; newValue: string } => {
  const uboTypes = uboMDMS.uboType ?? [];

  const getDisplayValue = (value?: string): string => {
    if (!value) return '';

    const match = uboTypes.find(
      (ubo) => ubo.key === value || ubo.values?.some((val) => val.key === value)
    );

    if (!match) {
      return resolveRiskLabel(value, auditDiff.fieldName, brokerList) ?? '';
    }

    // If top-level key matched, return displayText
    if (match.key === value) {
      return match.displayText ?? '';
    }

    // Else if inner value matched, return label
    const innerMatch = match.values?.find((val) => val.key === value);
    if (innerMatch) {
      return innerMatch.label ?? '';
    }

    return '';
  };

  return {
    oldValue: getDisplayValue(auditDiff.oldValue),
    newValue: getDisplayValue(auditDiff.newValue),
  };
};

export const isUsPerson = (usPerson: string | undefined | null): boolean => {
  return (
    typeof usPerson === 'string' && !!usPerson && usPerson?.toString()?.toLowerCase() === 'yes'
  );
};

export const isTaxResidentOtherThanInd = (value: string | boolean | undefined | null): boolean => {
  return (typeof value === 'string' || typeof value === 'boolean') && value?.toString() === 'true';
};

function getStatusString(
  role: string,
  status: string,
  roleType: 'approver1' | 'approver2' | undefined,
  master: { [key: string]: { [key: string]: string | { [key: string]: string } } }
): string {
  const value = master[role]?.[status];

  if (!value) return APPLICATION_LISTING_STATUS[status] || status;

  if (typeof value === 'string') return value; // POA, reviewer, etc.

  // If it's an object (approver1 / approver2)
  if (roleType && value[roleType]) return value[roleType];

  // If no matching roleType, use default if exists
  if (value.default) return value.default;

  // ❌ Prevent falling back to the first value (this caused Approver 1 seeing Approver 2 Pending)
  return APPLICATION_LISTING_STATUS[status] || status;
}

export function resolveApplicationStatus(
  application: any,
  role: string,
  roleType?: string
): string {
  const normalizedRoleType =
    roleType === 'approver1' || roleType === 'approver2' ? roleType : undefined;

  // reviewer check first
  if (role === USER_ROLES.KFIN_REVIEWER) {
    return getStatusString(
      role,
      application.status,
      normalizedRoleType,
      ApplicationStatusBasedOnRole
    );
  }

  if (application.hasPOA) {
    return getStatusString(
      role,
      application.status,
      normalizedRoleType,
      ApplicationStatusBasedOnRole
    );
  }

  if (checkIfApplicationIsNonIndividual(application)) {
    return getStatusString(
      role,
      application.status,
      normalizedRoleType,
      ApplicationStatusBasedOnRole
    );
  }

  if (!checkIfApplicationIsNonIndividual(application) && role === USER_ROLES.AMCAPPROVER) {
    return getStatusString(
      role,
      application.status,
      normalizedRoleType,
      IndividualApplicationStatusBasedOnRole
    );
  }

  // fallback
  return getStatusString(
    role,
    application.status,
    normalizedRoleType,
    APPLICATION_LISTING_STATUS as any
  );
}

export const Maker = (role: USER_ROLES): boolean =>
  [USER_ROLES.RM, USER_ROLES.DISTRIBUTOR, USER_ROLES.SUBDISTRIBUTOR].includes(role);

export const statusRoleBased = (role: USER_ROLES, application: ApplicationProps | null): string => {
  return Maker(role) &&
    (application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ||
      AMC_APPROVER_CHECK_FOR_INDIVIDUAL)
    ? 'draft'
    : application?.status || '';
};

// export const decodeState = (
//   stateValue = ''
// ): null | { mobile: string; applicationId: string; name: string; role: string } => {
//   if (!stateValue) return null;
//   return JSON.parse(atob(stateValue));
// };

export function addEqualOperator(str: string): string {
  // Base64 strings must be a multiple of 4 in length.
  const paddingNeeded = (4 - (str.length % 4)) % 4;

  return str + '='.repeat(paddingNeeded);
}

export function decodeBase64(
  encoded: string
): null | { phone: string; applicationId: string; name: string; role: string } {
  // Step 1: Decode outer base64
  const decodedOuter = atob(encoded);

  // console.log('encoded:', encoded);
  // console.log('decodedOuter:', decodedOuter);
  // console.log('SALT:', SALT);
  // console.log('SALT2:', SALT2);

  // Step 2: Remove SALT + SALT2
  if (!decodedOuter.startsWith(RISK_SALT) || !decodedOuter.endsWith(RISK_SALT2)) {
    throw new Error('Invalid encoded format');
  }

  const withoutSalts = decodedOuter.substring(
    RISK_SALT.length,
    decodedOuter.length - RISK_SALT2.length
  );

  // Step 3: Add back "=" padding removed earlier
  const padded = addEqualOperator(withoutSalts);

  // Step 4: Decode inner base64 to get final value
  const finalValue = atob(padded);

  return JSON.parse(finalValue);
}

// Allow only numbers with up to 2 decimal places
export const allowTwoDecimals = (value: string): boolean => {
  const regex = /^\d*\.?\d{0,2}$/;
  return regex.test(value);
};

// Format to 2 decimals on blur
export const formatTwoDecimals = (value: string | number): string => {
  if (value === '' || value === null || value === undefined) return '';
  const num = parseFloat(String(value));
  if (isNaN(num)) return '';
  return num.toFixed(2);
};

export const isPhysicallyChallenged = (
  isPhysicallyDisabled: string | undefined | null
): boolean => {
  if (!isPhysicallyDisabled) return false;
  return YesNoMaster[isPhysicallyDisabled] === YesNoMaster.yes;
};

export const excludeReviewrField = (name: string) => {
  return ![
    'negativeMatchFound',
    'worldCheckScreeningStatus',
    'typeOfExceptionFound',
    'otherTypeOfExceptionFound',
    'rejectedReason',
    'comment',
  ].includes(name);
};

export function isAccreditedInvestorApplicable(accreditedInvestorApplicable: string): boolean {
  return accreditedInvestorApplicable?.toLowerCase() === 'yes';
}

export const isFirstApplicant = (applicantType: string): boolean => {
  return ['firstapplicant', '1'].includes(applicantType || '');
};
